/**
 * RealtimeChatPanel Component
 * Real-time messaging panel with WebSocket connection, typing indicators,
 * online presence, message reactions, and infinite scroll
 * Based on ImpactIQ v2 architecture patterns
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useRealtimeMessages, Reaction } from '../../hooks/useRealtimeMessages';
import TypingIndicator from '../TypingIndicator/TypingIndicator';
import OnlineStatus from '../OnlineStatus/OnlineStatus';
import { mediaAPI, aiAPI, unlockAPI, PushEmailTone } from '../../services/api';
import { toast } from '../Toast/Toast';
import MediaPreview from '../MediaPreview/MediaPreview';
import { decodeHtmlEntities } from '../../utils/decodeHtmlEntities';
import { haptic } from '../../utils/haptic';
import './RealtimeChatPanel.css';

const AI_TONE_STORAGE_KEY = 'oj_chat_ai_tone';
const AI_LENGTH_STORAGE_KEY = 'oj_chat_ai_length';
type AiLength = 'short' | 'medium' | 'long';
// All 5 chat tones are sexual variants. Push-email uses the same `tone`
// keys but with different prompt mappings on the backend — these labels
// + hints are scoped to the chat composer.
// W5.5: default quick-reply templates. Admin can override per-browser
// via localStorage key oj_chat_templates_v1 (JSON array of strings).
const DEFAULT_QUICK_REPLIES = [
  'Got it 👍',
  'On its way!',
  'Sending now',
  'Will get back to you shortly',
  'Thanks for being part of the OJ fam ❤️',
  'Refund processed — check your email',
  'New drop coming this week — stay tuned',
];

function loadQuickReplies(): string[] {
  if (typeof localStorage === 'undefined') return DEFAULT_QUICK_REPLIES;
  try {
    const raw = localStorage.getItem('oj_chat_templates_v1');
    if (!raw) return DEFAULT_QUICK_REPLIES;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every(s => typeof s === 'string')) {
      return parsed.length ? parsed : DEFAULT_QUICK_REPLIES;
    }
  } catch {}
  return DEFAULT_QUICK_REPLIES;
}

const AI_TONE_OPTIONS: Array<{ value: PushEmailTone; label: string; hint: string }> = [
  { value: 'flirty',   label: 'Blunt',     hint: 'Direct sexual interest — "I want you", "send a pic"' },
  { value: 'bold',     label: 'Dominant',  hint: 'Commanding, in-charge — "do it now", "show me"' },
  { value: 'personal', label: 'Intimate',  hint: 'Sweet + horny — "miss you baby", "thinking about you"' },
  { value: 'urgent',   label: 'Right Now', hint: 'Hot-and-now urgency — "come thru tonight"' },
  { value: 'friendly', label: 'Tease',     hint: 'Slow-burn edging — "you want it bad don\'t you"' },
];

interface RealtimeChatPanelProps {
  partnerId: string;
  partnerName: string;
  partnerAvatar: string;
  currentUserId: string;
  isAdmin?: boolean;  // Whether current user is admin
  onClose?: () => void;
  /** Called after a message is successfully sent. Parent uses this to
   *  refresh the inbox preview so the new conversation + latest message
   *  appears in the list immediately instead of waiting for the next
   *  mount/poll. */
  onMessageSent?: () => void;
}

const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🔥'];

const RealtimeChatPanel: React.FC<RealtimeChatPanelProps> = ({
  partnerId,
  partnerName,
  partnerAvatar,
  currentUserId,
  isAdmin = false,
  onClose,
  onMessageSent,
}) => {
  const [input, setInput] = useState('');
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [showReactionPicker, setShowReactionPicker] = useState<string | null>(null);
  const [contextMenuMessageId, setContextMenuMessageId] = useState<string | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<{ file: File; url: string; name: string } | null>(null);
  const [uploading, setUploading] = useState(false);

  // v2 composer state
  const [showTray, setShowTray] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);

  // Inline-paywall "unlock drop" composer (admin-only). Opens from the +
  // tray, prompts for price/title/teaser/locked-content, posts to the
  // admin API, and inserts the shareable URL into the message input so
  // the admin can review before tapping send.
  const [showDropModal, setShowDropModal] = useState(false);
  const [dropPrice, setDropPrice] = useState('5');
  const [dropTitle, setDropTitle] = useState('');
  const [dropDescription, setDropDescription] = useState('');
  const [dropBody, setDropBody] = useState('');
  const [dropCreating, setDropCreating] = useState(false);

  // AI reply drafting (admin-only)
  const [aiDrafting, setAiDrafting] = useState(false);

  // Track scroll position for the floating "jump to bottom" indicator.
  // Re-renders whenever the user scrolls so the button can show/hide.
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  const [aiTone, setAiTone] = useState<PushEmailTone>(() => {
    if (typeof window === 'undefined') return 'flirty';
    const saved = localStorage.getItem(AI_TONE_STORAGE_KEY) as PushEmailTone | null;
    const valid = AI_TONE_OPTIONS.some(t => t.value === saved);
    return valid && saved ? saved : 'flirty';
  });
  // v2: output length preference. Persisted across sessions so admin
  // doesn't have to re-pick after every reload.
  const [aiLength, setAiLength] = useState<AiLength>(() => {
    if (typeof window === 'undefined') return 'medium';
    const saved = localStorage.getItem(AI_LENGTH_STORAGE_KEY) as AiLength | null;
    return saved === 'short' || saved === 'long' ? saved : 'medium';
  });
  const [showToneMenu, setShowToneMenu] = useState(false);
  // W5.3: in-conversation search. Toggle reveals an input above the
  // message list; non-empty query filters messages to those whose
  // content matches (case-insensitive substring).
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // W5.5: admin quick-reply templates. Stored in localStorage so admin
  // can curate the set across sessions without a backend round-trip.
  const [showTemplates, setShowTemplates] = useState(false);
  // Templates are edited via console (see popover hint); only read
  // localStorage when the popover is open, keeping it off the render
  // path during normal typing.
  const quickReplies = useMemo(
    () => (showTemplates ? loadQuickReplies() : []),
    [showTemplates],
  );
  // W5.6: per-conversation admin notes (private, never sent to subscriber).
  // Persisted to localStorage keyed by partnerId.
  const adminNoteKey = `oj_admin_note_${partnerId}`;
  const [showNotes, setShowNotes] = useState(false);
  const [adminNote, setAdminNote] = useState<string>(() => {
    if (typeof localStorage === 'undefined') return '';
    return localStorage.getItem(adminNoteKey) || '';
  });
  // Re-load when conversation changes
  useEffect(() => {
    if (typeof localStorage !== 'undefined') {
      setAdminNote(localStorage.getItem(`oj_admin_note_${partnerId}`) || '');
      setShowNotes(false);
    }
  }, [partnerId]);
  const persistAdminNote = (value: string) => {
    setAdminNote(value);
    try {
      if (value) localStorage.setItem(adminNoteKey, value);
      else localStorage.removeItem(adminNoteKey);
    } catch {}
  };

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordChunksRef = useRef<Blob[]>([]);
  const recordTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isAtBottomRef = useRef(true);
  const prevMessagesLengthRef = useRef(0);

  const {
    messages,
    connected,
    connecting,
    otherUserTyping,
    otherUserOnline,
    error,
    hasMore,
    loadingMore,
    sendMessage,
    editMessage,
    deleteMessage,
    toggleReaction,
    sendTyping,
    loadMore,
    refresh,
  } = useRealtimeMessages(partnerId);

  // W8.2: pull-to-refresh state. When user drags down at top of list,
  // we measure offset and trigger refresh() once they cross threshold.
  const pullStartYRef = useRef<number | null>(null);
  const [pullOffset, setPullOffset] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const PULL_THRESHOLD = 70;
  const handleTouchStart = (e: React.TouchEvent) => {
    const c = messagesContainerRef.current;
    if (!c) return;
    if (c.scrollTop <= 0) pullStartYRef.current = e.touches[0].clientY;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (pullStartYRef.current == null) return;
    const dy = e.touches[0].clientY - pullStartYRef.current;
    if (dy > 0) {
      // Dampen the pull so it feels natural (asymptotic toward 120px).
      const eased = Math.min(120, dy * 0.5);
      setPullOffset(eased);
    } else {
      setPullOffset(0);
    }
  };
  const handleTouchEnd = async () => {
    const offset = pullOffset;
    pullStartYRef.current = null;
    if (offset >= PULL_THRESHOLD && !refreshing) {
      setRefreshing(true);
      try { await refresh(); } catch {}
      setRefreshing(false);
    }
    setPullOffset(0);
  };

  // Check if scrolled to bottom
  const checkIfAtBottom = useCallback(() => {
    if (!messagesContainerRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    return scrollHeight - scrollTop - clientHeight < 100;
  }, []);

  // Scroll to bottom
  const scrollToBottom = useCallback((smooth = true) => {
    messagesEndRef.current?.scrollIntoView({
      behavior: smooth ? 'smooth' : 'auto',
    });
  }, []);

  // Scroll to top of CURRENTLY LOADED messages — triggers infinite-scroll
  // load-more (the handleScroll effect already wires that on scrollTop<50).
  const scrollToTop = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  // Auto-scroll on new messages (only if already at bottom)
  useEffect(() => {
    if (messages.length > prevMessagesLengthRef.current && isAtBottomRef.current) {
      scrollToBottom();
    }
    prevMessagesLengthRef.current = messages.length;
  }, [messages.length, scrollToBottom]);

  // Handle scroll for infinite scroll and tracking position
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;

    const atBottom = checkIfAtBottom();
    isAtBottomRef.current = atBottom;
    // Show "jump to bottom" button when user is meaningfully scrolled up.
    setShowJumpToBottom(!atBottom);

    // Load more when scrolled to top
    const { scrollTop } = messagesContainerRef.current;
    if (scrollTop < 50 && hasMore && !loadingMore) {
      loadMore();
    }
  }, [hasMore, loadingMore, loadMore, checkIfAtBottom]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close context menu on outside click
  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenuMessageId(null);
      setShowReactionPicker(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // W5.4: dump the loaded message buffer to CSV. Note this only covers
  // messages currently in memory (post-loadMore from REST) — older
  // messages beyond the cursor would need a server-side export endpoint,
  // which we can add later if needed. For typical conversations this is
  // sufficient.
  const handleExportChatCsv = () => {
    const cols: Array<[string, (m: any) => any]> = [
      ['timestamp_iso', m => m.created_at || ''],
      ['direction',     m => (m.from_user_id === currentUserId || m.from_user_id === 'self' || (isAdmin && m.from_user_id === 'admin')) ? 'sent' : 'received'],
      ['sender_id',     m => m.from_user_id || ''],
      ['content',       m => m.content || ''],
      ['content_type',  m => m.content_type || 'text'],
      ['attachments',   m => Array.isArray(m.attachments) ? m.attachments.map((a: any) => typeof a === 'string' ? a : a.url).join(' | ') : ''],
      ['edited',        m => m.edited ? '1' : '0'],
      ['status',        m => m.status || ''],
      ['message_id',    m => m.id || ''],
    ];
    const esc = (v: any) => {
      const str = v == null ? '' : String(v);
      return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const header = cols.map(c => c[0]).join(',');
    const body = messages.map(m => cols.map(c => esc(c[1](m))).join(',')).join('\r\n');
    const csv = `${header}\r\n${body}\r\n`;
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeName = (partnerName || partnerId).replace(/[^a-z0-9_-]+/gi, '_').slice(0, 40);
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `chat-${safeName}-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSend = async () => {
    if (!input.trim() && !attachmentPreview) return;

    let attachmentUrls: string[] | undefined;

    if (attachmentPreview) {
      setUploading(true);
      try {
        const result = await mediaAPI.uploadFile(attachmentPreview.file);
        attachmentUrls = [result.url];
      } catch (err) {
        console.error('Upload failed:', err);
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    const content = input.trim() || (attachmentPreview ? attachmentPreview.name : '');
    const success = await sendMessage(content, attachmentUrls);
    if (success) {
      haptic('tap');
      setInput('');
      setAttachmentPreview(null);
      inputRef.current?.focus();
      scrollToBottom();
      // Notify parent so the inbox preview can re-fetch and show the
      // new conversation + latest message. Without this, the inbox is
      // stale until the page is reloaded.
      onMessageSent?.();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setAttachmentPreview({ file, url, name: file.name });
    setShowTray(false);
    e.target.value = '';
  };

  const handleRemoveAttachment = () => {
    if (attachmentPreview) URL.revokeObjectURL(attachmentPreview.url);
    setAttachmentPreview(null);
  };

  // Generate an inline-paywall drop from the modal. On success we paste
  // the URL into the message input so the admin can review the unfurl
  // preview before sending (matches the existing attachment-preview flow
  // where the user confirms before the message actually sends).
  const handleCreateDrop = async () => {
    const priceNum = parseFloat(dropPrice);
    if (!Number.isFinite(priceNum) || priceNum < 0.5) {
      toast.error('Price must be at least $0.50');
      return;
    }
    if (!dropTitle.trim()) {
      toast.error('Add a title for the drop');
      return;
    }
    setDropCreating(true);
    try {
      const res = await unlockAPI.createDrop({
        price: priceNum,
        title: dropTitle.trim(),
        description: dropDescription.trim(),
        body: dropBody,
      });
      if (!res?.success || !res.url) {
        toast.error(res?.error || 'Could not create drop');
        return;
      }
      // Append the URL to whatever's already typed (with a space separator).
      setInput(prev => (prev.trim() ? `${prev.trim()} ${res.url}` : res.url));
      setShowDropModal(false);
      setDropTitle('');
      setDropDescription('');
      setDropBody('');
      // Leave dropPrice as-is so consecutive drops at the same price feel natural.
      toast.success('Drop ready — tap send to share');
    } catch (err: any) {
      toast.error(err?.message || 'Drop creation failed');
    } finally {
      setDropCreating(false);
    }
  };

  // --- Voice recording (MediaRecorder API) ------------------------------
  const startVoiceRecording = async () => {
    if (recording) return;
    setShowTray(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime =
        typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm';
      const mr = new MediaRecorder(stream, { mimeType: mime });
      recordChunksRef.current = [];
      mr.ondataavailable = ev => { if (ev.data.size > 0) recordChunksRef.current.push(ev.data); };
      mr.onstop = () => {
        const blob = new Blob(recordChunksRef.current, { type: mime });
        const f = new File([blob], `voice-${Date.now()}.webm`, { type: mime });
        const url = URL.createObjectURL(f);
        setAttachmentPreview({ file: f, url, name: 'Voice message' });
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      setRecordSeconds(0);
      recordTimerRef.current = setInterval(() => setRecordSeconds(s => s + 1), 1000);
    } catch (err) {
      console.warn('Mic permission denied or unavailable:', err);
      toast.error('Microphone access is required to record voice messages.');
    }
  };

  const stopVoiceRecording = (sendImmediately: boolean) => {
    const mr = mediaRecorderRef.current;
    if (recordTimerRef.current) { clearInterval(recordTimerRef.current); recordTimerRef.current = null; }
    setRecording(false);
    if (!mr) return;
    if (mr.state !== 'inactive') mr.stop();
    if (!sendImmediately) {
      // Discard — drop chunks and remove preview that onstop will create
      recordChunksRef.current = [];
      setTimeout(() => setAttachmentPreview(null), 0);
    }
  };

  // Cleanup recording timer on unmount
  useEffect(() => {
    return () => {
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
      if (mediaRecorderRef.current?.state === 'recording') {
        try { mediaRecorderRef.current.stop(); } catch {}
      }
    };
  }, []);

  const formatRecordTime = (s: number) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, '0')}`;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
    sendTyping();
  };

  // Admin-only: ask Workers AI to draft a reply based on the last 12
  // messages in this conversation. If the input box has text, treat it
  // as a steering intent ("make it flirty", "decline politely", etc).
  // The drafted reply lands in the input so the admin can edit/send.
  const handleAiDraftReply = async () => {
    if (!isAdmin) return;
    if (aiDrafting) return;
    if (messages.length === 0) return;
    setAiDrafting(true);
    setShowToneMenu(false);
    try {
      const intent = input.trim() || undefined;
      const res = await aiAPI.draftReply({
        conversationId: partnerId,
        intent,
        tone: aiTone,
        length: aiLength,
      });
      if (res?.reply) {
        setInput(res.reply);
        // Restore focus so admin can hit Enter or tweak.
        inputRef.current?.focus();
      }
    } catch (err: any) {
      console.error('AI reply draft failed:', err);
      toast.error(err?.message || 'AI reply failed. Try again.');
    } finally {
      setAiDrafting(false);
    }
  };

  const handleStartEdit = (messageId: string, content: string) => {
    setEditingMessageId(messageId);
    setEditContent(content);
    setContextMenuMessageId(null);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditContent('');
  };

  const handleSaveEdit = async () => {
    if (!editingMessageId || !editContent.trim()) return;

    const success = await editMessage(editingMessageId, editContent.trim());
    if (success) {
      handleCancelEdit();
      // Inbox preview is denormalized — refresh so the edited content
      // shows in the conversation list, not the pre-edit text.
      onMessageSent?.();
    }
  };

  const handleEditKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleDelete = async (messageId: string) => {
    if (window.confirm('Delete this message?')) {
      await deleteMessage(messageId);
    }
    setContextMenuMessageId(null);
  };

  // Admin-only: hard-delete removes the row from D1 entirely (no soft
  // tombstone). Use when you need to PURGE a message — accidental sends,
  // test data, anything you don't want lingering as a deleted_at row.
  const handleHardDelete = async (messageId: string) => {
    if (!isAdmin) return;
    if (window.confirm('Permanently delete this message? This cannot be undone — the row will be purged from the database.')) {
      const ok = await deleteMessage(messageId, { hard: true });
      if (ok) toast.success('Message purged.');
      else toast.error('Permanent delete failed.');
    }
    setContextMenuMessageId(null);
  };

  const handleReaction = async (messageId: string, emoji: string) => {
    await toggleReaction(messageId, emoji);
    setShowReactionPicker(null);
  };

  const handleContextMenu = (e: React.MouseEvent, messageId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuMessageId(messageId);
    setShowReactionPicker(null);
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  // W5.3: filter then group. Empty/whitespace query = passthrough.
  // Memoized so long conversations don't re-filter + re-group on every
  // composer keystroke (input state lives in this component).
  const trimmedQuery = searchQuery.trim().toLowerCase();
  const visibleMessages = useMemo(
    () => trimmedQuery
      ? messages.filter(m => (m.content || '').toLowerCase().includes(trimmedQuery))
      : messages,
    [messages, trimmedQuery],
  );

  const groupedMessages = useMemo(
    () => visibleMessages.reduce((groups, msg) => {
      const date = formatDate(msg.created_at);
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
      return groups;
    }, {} as Record<string, typeof messages>),
    [visibleMessages],
  );

  // Render reactions
  const renderReactions = (reactions: Reaction[] | undefined, messageId: string) => {
    if (!reactions || reactions.length === 0) return null;

    return (
      <div className="message-reactions">
        {reactions.map((reaction) => (
          <button
            key={reaction.emoji}
            className={`reaction-badge ${reaction.includesMe ? 'mine' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleReaction(messageId, reaction.emoji);
            }}
            title={reaction.users.join(', ')}
          >
            <span className="reaction-emoji">{reaction.emoji}</span>
            <span className="reaction-count">{reaction.count}</span>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="realtime-chat-panel">
      {/* Header */}
      <div className="chat-panel-header">
        {onClose && (
          <button
            className="rtc-back-btn"
            onClick={onClose}
            aria-label="Back to inbox"
            type="button"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
        )}
        <div className="chat-partner-info">
          <div className="partner-avatar-wrapper">
            <img src={partnerAvatar || ''} alt={partnerName} className="partner-avatar" onError={e => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }} />
            {otherUserOnline && <span className="avatar-online-dot"></span>}
          </div>
          <div className="partner-details">
            <span className="partner-name">{partnerName}</span>
            <OnlineStatus isOnline={otherUserOnline} size="sm" />
          </div>
        </div>
        <div className="connection-status">
          {connecting && (
            <span className="status-connecting">Connecting...</span>
          )}
          {connected && (
            <span className="status-connected">
              <span className="connected-dot"></span>
              Live
            </span>
          )}
          {!connected && !connecting && error && (
            <span className="status-error" title={error}>Offline mode</span>
          )}
        </div>
        <button
          className={`chat-search-toggle${showSearch ? ' active' : ''}`}
          onClick={() => {
            setShowSearch(s => {
              if (s) setSearchQuery('');
              return !s;
            });
          }}
          aria-label={showSearch ? 'Close search' : 'Search this conversation'}
          title="Search this conversation"
          type="button"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <circle cx="11" cy="11" r="7"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
        </button>
        {/* W5.4: chat export to CSV — admin-only affordance for keeping
            a record outside the app (e.g. for offer evidence, support). */}
        {isAdmin && (
          <button
            className="chat-search-toggle"
            onClick={() => handleExportChatCsv()}
            aria-label="Export this chat to CSV"
            title="Export this chat to CSV"
            type="button"
            disabled={messages.length === 0}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
        )}
        {/* W5.6: private admin notes — never sent to subscriber */}
        {isAdmin && (
          <button
            className={`chat-search-toggle${showNotes ? ' active' : ''}${adminNote ? ' has-content' : ''}`}
            onClick={() => setShowNotes(v => !v)}
            aria-label={showNotes ? 'Hide private notes' : 'Show private notes'}
            title="Private admin notes (not visible to subscriber)"
            type="button"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="9" y1="13" x2="15" y2="13"/>
              <line x1="9" y1="17" x2="13" y2="17"/>
            </svg>
          </button>
        )}
        {onClose && (
          <button className="close-chat-btn" onClick={onClose} aria-label="Close chat">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      {showSearch && (
        <div className="chat-search-row">
          <input
            type="text"
            className="chat-search-input"
            placeholder="Search messages…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            autoFocus
          />
          {trimmedQuery && (
            <span className="chat-search-count">
              {visibleMessages.length} match{visibleMessages.length === 1 ? '' : 'es'}
            </span>
          )}
        </div>
      )}

      {isAdmin && showNotes && (
        <div className="chat-notes-row">
          <textarea
            className="chat-notes-input"
            placeholder="Private notes about this subscriber — never sent to them."
            value={adminNote}
            onChange={e => persistAdminNote(e.target.value)}
            rows={3}
          />
          <span className="chat-notes-hint">
            {adminNote ? `Saved locally · ${adminNote.length} chars` : 'Notes save automatically'}
          </span>
        </div>
      )}

      {/* Messages */}
      <div
        className="chat-messages-container"
        ref={messagesContainerRef}
        onScroll={handleScroll}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={pullOffset > 0 ? { transform: `translateY(${pullOffset}px)`, transition: refreshing ? 'transform 0.2s' : 'none' } : { transition: 'transform 0.2s' }}
      >
        {/* W8.2: pull-to-refresh indicator */}
        {(pullOffset > 0 || refreshing) && (
          <div className="chat-pull-indicator" style={{ opacity: Math.min(1, pullOffset / PULL_THRESHOLD) }}>
            {refreshing
              ? <><span className="chat-pull-spinner" /> Refreshing…</>
              : pullOffset >= PULL_THRESHOLD ? 'Release to refresh' : 'Pull to refresh'}
          </div>
        )}
        {/* Load more indicator */}
        {loadingMore && (
          <div className="loading-more-indicator">
            <span className="loading-spinner"></span>
            Loading older messages...
          </div>
        )}

        {hasMore && !loadingMore && messages.length > 0 && (
          <button className="load-more-btn" onClick={loadMore}>
            Load older messages
          </button>
        )}

        {Object.entries(groupedMessages).map(([date, msgs]) => (
          <div key={date} className="message-date-group">
            <div className="date-divider">
              <span>{date}</span>
            </div>
            {msgs.map((msg) => {
              // Message is "sent" if: from current user, optimistic update, OR admin viewing admin messages
              const isSent = msg.from_user_id === currentUserId
                || msg.from_user_id === 'self'
                || (isAdmin && msg.from_user_id === 'admin');
              const isEditing = editingMessageId === msg.id;

              return (
                <div
                  key={msg.id}
                  className={`chat-message ${isSent ? 'sent' : 'received'} ${isEditing ? 'editing' : ''}`}
                  onContextMenu={(e) => isSent && handleContextMenu(e, msg.id)}
                >
                  {!isSent && (
                    <img src={partnerAvatar || ''} alt="" className="message-avatar" onError={e => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }} />
                  )}
                  <div className="message-bubble">
                    {isEditing ? (
                      <div className="edit-message-form">
                        <input
                          type="text"
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          onKeyDown={handleEditKeyPress}
                          autoFocus
                          className="edit-input"
                        />
                        <div className="edit-actions">
                          <button className="edit-cancel-btn" onClick={handleCancelEdit}>
                            Cancel
                          </button>
                          <button className="edit-save-btn" onClick={handleSaveEdit}>
                            Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="message-attachments">
                            {msg.attachments.map((att: any, idx: number) => {
                              const url = typeof att === 'string' ? att : att.url;
                              const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(url) || url.includes('image');
                              return isImage ? (
                                <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                                  <img src={url} alt="attachment" className="message-attachment-img" />
                                </a>
                              ) : (
                                <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="message-file-link">
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                                  </svg>
                                  {typeof att === 'string' ? 'Attachment' : (att.name || 'File')}
                                </a>
                              );
                            })}
                          </div>
                        )}
                        {msg.content && <p className="message-content">{decodeHtmlEntities(msg.content)}</p>}
                        {msg.edited && (
                          <span className="edited-label">(edited)</span>
                        )}
                      </>
                    )}
                    {renderReactions(msg.reactions, msg.id)}
                    <div className="message-meta">
                      <span className="message-time">{formatTime(msg.created_at)}</span>
                      {isSent && msg.status && (
                        <span className={`message-status ${msg.status}`}>
                          {msg.status === 'pending' && (
                            <svg viewBox="0 0 24 24" className="status-icon">
                              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="32" strokeDashoffset="32">
                                <animate attributeName="stroke-dashoffset" values="32;0" dur="1s" repeatCount="indefinite" />
                              </circle>
                            </svg>
                          )}
                          {msg.status === 'sent' && '✓'}
                          {msg.status === 'delivered' && '✓✓'}
                          {msg.status === 'read' && (
                            <span className="read-check">✓✓</span>
                          )}
                          {msg.status === 'failed' && (
                            <span className="failed-icon" title="Failed to send">!</span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Message actions */}
                  {isSent && !isEditing && (
                    <div className="message-actions">
                      <button
                        className="action-btn reaction-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowReactionPicker(showReactionPicker === msg.id ? null : msg.id);
                          setContextMenuMessageId(null);
                        }}
                        aria-label="Add reaction"
                      >
                        😊
                      </button>
                      <button
                        className="action-btn more-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setContextMenuMessageId(contextMenuMessageId === msg.id ? null : msg.id);
                          setShowReactionPicker(null);
                        }}
                        aria-label="More options"
                      >
                        ⋮
                      </button>
                    </div>
                  )}

                  {/* Reaction picker */}
                  {showReactionPicker === msg.id && (
                    <div className="reaction-picker" onClick={(e) => e.stopPropagation()}>
                      {REACTION_EMOJIS.map((emoji) => (
                        <button
                          key={emoji}
                          className="reaction-option"
                          onClick={() => handleReaction(msg.id, emoji)}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Context menu */}
                  {contextMenuMessageId === msg.id && isSent && (
                    <div className="context-menu" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="context-menu-item"
                        onClick={() => handleStartEdit(msg.id, decodeHtmlEntities(msg.content))}
                      >
                        Edit
                      </button>
                      <button
                        className="context-menu-item danger"
                        onClick={() => handleDelete(msg.id)}
                      >
                        Delete
                      </button>
                      {isAdmin && (
                        <button
                          className="context-menu-item danger context-menu-item--purge"
                          onClick={() => handleHardDelete(msg.id)}
                          title="Permanently remove this message from the database. Cannot be undone."
                        >
                          🗑️ Permanently delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        {/* Typing indicator */}
        {otherUserTyping && (
          <div className="typing-indicator-row">
            <img src={partnerAvatar || ''} alt="" className="message-avatar" onError={e => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }} />
            <TypingIndicator />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Scroll navigation — floating buttons that jump to top (load older
          history) or bottom (latest message). Always available so long
          conversations are navigable. Top button uses the existing
          infinite-scroll trigger; bottom button only shows when user is
          scrolled up. */}
      <div className="rtc-scroll-nav">
        <button
          type="button"
          className="rtc-scroll-btn rtc-scroll-up"
          onClick={scrollToTop}
          title="Jump to top (loads older messages)"
          aria-label="Scroll to top"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
            <polyline points="18 15 12 9 6 15"/>
          </svg>
        </button>
        {showJumpToBottom && (
          <button
            type="button"
            className="rtc-scroll-btn rtc-scroll-down"
            onClick={() => scrollToBottom(true)}
            title="Jump to latest message"
            aria-label="Scroll to bottom"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
        )}
      </div>

      {/* Hidden file inputs — separated by intent so mobile camera/photo/video
          pickers get the right OS sheet. The Camera input uses `capture` to
          launch the live camera on phones; Photos/Video pick from gallery. */}
      <input ref={fileInputRef} type="file" accept="image/*,video/*,.pdf,.doc,.docx,.txt" style={{ display: 'none' }} onChange={handleFileSelect} />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleFileSelect} />
      <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileSelect} />
      <input ref={videoInputRef} type="file" accept="video/*" style={{ display: 'none' }} onChange={handleFileSelect} />

      {/* Voice recording bar (replaces composer while active) */}
      {recording && (
        <div className="rtc-recording-bar" role="status" aria-live="polite">
          <span className="rtc-record-dot" aria-hidden="true" />
          <span className="rtc-record-time">{formatRecordTime(recordSeconds)}</span>
          <span className="rtc-record-label">Recording voice…</span>
          <button className="rtc-record-cancel" onClick={() => stopVoiceRecording(false)} aria-label="Cancel recording">Cancel</button>
          <button className="rtc-record-send" onClick={() => stopVoiceRecording(true)} aria-label="Stop and attach">
            <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
          </button>
        </div>
      )}

      {/* Image / video attachment preview — full-screen overlay so the
          media gets the whole viewport instead of being cropped into a
          thin bar. Audio + arbitrary-file previews stay inline below. */}
      {!recording && attachmentPreview && (attachmentPreview.file.type.startsWith('image/') || attachmentPreview.file.type.startsWith('video/')) && (
        <MediaPreview
          fileUrl={attachmentPreview.url}
          fileType={attachmentPreview.file.type.startsWith('video/') ? 'video' : 'image'}
          caption={input}
          sending={uploading}
          onCaptionChange={setInput}
          onClose={handleRemoveAttachment}
          onSend={handleSend}
        />
      )}

      {/* Audio / non-media attachment — inline preview bar still makes sense. */}
      {!recording && attachmentPreview && !attachmentPreview.file.type.startsWith('image/') && !attachmentPreview.file.type.startsWith('video/') && (
        <div className="attachment-preview-bar">
          {attachmentPreview.file.type.startsWith('audio/') ? (
            <div className="attachment-audio-thumb" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3zM19 10v2a7 7 0 01-14 0v-2H3v2a9 9 0 008 8.94V23h2v-2.06A9 9 0 0021 12v-2h-2z"/></svg>
            </div>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
            </svg>
          )}
          <span className="attachment-name">{attachmentPreview.name}</span>
          <button className="remove-attachment-btn" onClick={handleRemoveAttachment} aria-label="Remove attachment">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      {/* Attachment tray — opens above composer when [+] tapped */}
      {!recording && showTray && (
        <div className={`rtc-tray${isAdmin ? ' rtc-tray--admin' : ''}`} role="menu">
          {isAdmin && (
            <button className="rtc-tray-item" onClick={() => { setShowDropModal(true); setShowTray(false); }}>
              <span className="rtc-tray-icon rtc-tray-icon--unlock">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
              </span>
              Unlock Drop
            </button>
          )}
          <button className="rtc-tray-item" onClick={() => cameraInputRef.current?.click()}>
            <span className="rtc-tray-icon rtc-tray-icon--camera">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
            </span>
            Camera
          </button>
          <button className="rtc-tray-item" onClick={() => photoInputRef.current?.click()}>
            <span className="rtc-tray-icon rtc-tray-icon--photo">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            </span>
            Photos
          </button>
          <button className="rtc-tray-item" onClick={() => videoInputRef.current?.click()}>
            <span className="rtc-tray-icon rtc-tray-icon--video">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
            </span>
            Video
          </button>
          <button className="rtc-tray-item" onClick={startVoiceRecording}>
            <span className="rtc-tray-icon rtc-tray-icon--voice">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/></svg>
            </span>
            Voice
          </button>
          <button className="rtc-tray-item" onClick={() => fileInputRef.current?.click()}>
            <span className="rtc-tray-icon rtc-tray-icon--file">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            </span>
            File
          </button>
        </div>
      )}

      {/* Unlock Drop composer modal (admin only). On submit, the resulting
          shareable URL is pasted into the main message input so it sends
          like any other message — chat clients will unfurl the URL into a
          paywall card via the OG meta tags on the unlock page. */}
      {showDropModal && isAdmin && (
        <div className="rtc-drop-overlay" role="dialog" aria-modal="true" aria-label="Create unlock drop">
          <div className="rtc-drop-modal">
            <div className="rtc-drop-header">
              <span className="rtc-drop-title">Create unlock drop</span>
              <button
                className="rtc-drop-close"
                onClick={() => !dropCreating && setShowDropModal(false)}
                aria-label="Close"
                type="button"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="rtc-drop-body">
              <label className="rtc-drop-field">
                <span className="rtc-drop-label">Price (USD)</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0.5"
                  max="5000"
                  step="0.50"
                  value={dropPrice}
                  onChange={(e) => setDropPrice(e.target.value)}
                  disabled={dropCreating}
                />
              </label>
              <label className="rtc-drop-field">
                <span className="rtc-drop-label">Title <span className="rtc-drop-hint">(shown on the card)</span></span>
                <input
                  type="text"
                  maxLength={80}
                  placeholder="e.g. Late-night clip"
                  value={dropTitle}
                  onChange={(e) => setDropTitle(e.target.value)}
                  disabled={dropCreating}
                />
              </label>
              <label className="rtc-drop-field">
                <span className="rtc-drop-label">Teaser <span className="rtc-drop-hint">(shown under title in chat)</span></span>
                <input
                  type="text"
                  maxLength={140}
                  placeholder="Optional"
                  value={dropDescription}
                  onChange={(e) => setDropDescription(e.target.value)}
                  disabled={dropCreating}
                />
              </label>
              <label className="rtc-drop-field">
                <span className="rtc-drop-label">Locked content <span className="rtc-drop-hint">(revealed after payment)</span></span>
                <textarea
                  rows={4}
                  placeholder="Plain text / a link / instructions. Add image+video URLs later."
                  value={dropBody}
                  onChange={(e) => setDropBody(e.target.value)}
                  disabled={dropCreating}
                />
              </label>
            </div>
            <div className="rtc-drop-actions">
              <button
                type="button"
                className="rtc-drop-cancel"
                onClick={() => setShowDropModal(false)}
                disabled={dropCreating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rtc-drop-submit"
                onClick={handleCreateDrop}
                disabled={dropCreating}
              >
                {dropCreating ? 'Creating…' : 'Create + paste link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Input row — smart mic/send swap based on text presence */}
      {!recording && (
      <div className="chat-input-container">
        <button
          className={`attach-btn ${showTray ? 'open' : ''}`}
          onClick={() => setShowTray(v => !v)}
          disabled={uploading}
          aria-label={showTray ? 'Close attachments' : 'Open attachments'}
          aria-expanded={showTray}
          type="button"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {showTray ? <path d="M18 6L6 18M6 6l12 12"/> : <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>}
          </svg>
        </button>
        {isAdmin && (
          <div className="rtc-quick-replies">
            <button
              type="button"
              className={`rtc-quick-toggle${showTemplates ? ' is-open' : ''}`}
              onClick={() => setShowTemplates(v => !v)}
              aria-label="Quick reply templates"
              aria-expanded={showTemplates}
              title="Quick reply templates"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              </svg>
            </button>
            {showTemplates && (
              <>
                <div className="rtc-quick-backdrop" onClick={() => setShowTemplates(false)} />
                <div className="rtc-quick-popover" role="menu">
                  <div className="rtc-quick-heading">Quick replies</div>
                  {quickReplies.map(text => (
                    <button
                      key={text}
                      type="button"
                      className="rtc-quick-item"
                      onClick={() => {
                        setInput(prev => prev ? prev : text);
                        setShowTemplates(false);
                        inputRef.current?.focus();
                      }}
                    >
                      {text}
                    </button>
                  ))}
                  <div className="rtc-quick-hint">
                    Edit list in browser console: <code>localStorage.setItem('oj_chat_templates_v1', JSON.stringify([...]))</code>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        {isAdmin && (
          <div className="rtc-ai-controls">
            <button
              type="button"
              className={`rtc-ai-reply-btn ${aiDrafting ? 'is-drafting' : ''}`}
              onClick={handleAiDraftReply}
              disabled={aiDrafting || uploading || messages.length === 0}
              title={input.trim()
                ? `Draft AI reply (${aiTone}) with steer: "${input.trim().slice(0, 40)}…"`
                : `Draft AI reply (${aiTone}). Type a steer first or just click.`}
              aria-label="Draft AI reply"
            >
              {aiDrafting ? (
                <span className="rtc-ai-spinner" />
              ) : (
                <span className="rtc-ai-icon" aria-hidden="true">✨</span>
              )}
            </button>
            {/* Tone selector — small caret button opens a popover with
                 5 chips matching the push-email tones. Choice persists
                 in localStorage across sessions. */}
            <button
              type="button"
              className={`rtc-ai-tone-toggle ${showToneMenu ? 'is-open' : ''}`}
              onClick={() => setShowToneMenu(v => !v)}
              aria-haspopup="menu"
              aria-expanded={showToneMenu}
              aria-label={`AI tone: ${aiTone}. Tap to change.`}
              title={`AI tone: ${aiTone}. Tap to change.`}
            >
              <svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor">
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </button>
            {showToneMenu && (
              <>
                <div className="rtc-ai-tone-backdrop" onClick={() => setShowToneMenu(false)} />
                <div className="rtc-ai-tone-popover" role="menu">
                  <div className="rtc-ai-tone-heading">AI tone</div>
                  {AI_TONE_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`rtc-ai-tone-row ${aiTone === opt.value ? 'is-active' : ''}`}
                      onClick={() => {
                        setAiTone(opt.value);
                        try { localStorage.setItem(AI_TONE_STORAGE_KEY, opt.value); } catch {}
                        setShowToneMenu(false);
                      }}
                      role="menuitemradio"
                      aria-checked={aiTone === opt.value}
                    >
                      <span className="rtc-ai-tone-label">{opt.label}</span>
                      <span className="rtc-ai-tone-hint">{opt.hint}</span>
                    </button>
                  ))}
                  {/* Length toggle — short/medium/long. Lives inside the
                       same popover as tone since admin tweaks them at the
                       same time. Persists across sessions. */}
                  <div className="rtc-ai-tone-heading" style={{ marginTop: 10 }}>Length</div>
                  <div className="rtc-ai-length-row">
                    {(['short', 'medium', 'long'] as AiLength[]).map(len => (
                      <button
                        key={len}
                        type="button"
                        className={`rtc-ai-length-chip ${aiLength === len ? 'is-active' : ''}`}
                        onClick={() => {
                          setAiLength(len);
                          try { localStorage.setItem(AI_LENGTH_STORAGE_KEY, len); } catch {}
                        }}
                        aria-pressed={aiLength === len}
                        title={
                          len === 'short' ? 'One sentence, fast text vibe'
                          : len === 'medium' ? '2-4 sentences, default'
                          : 'Multi-sentence, built-out reply'
                        }
                      >
                        {len === 'short' ? 'S' : len === 'medium' ? 'M' : 'L'}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        <input
          ref={inputRef}
          type="text"
          placeholder={isAdmin ? 'Type a message — or ✨ for AI draft' : 'Type a message…'}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={uploading || aiDrafting}
          aria-label="Message input"
        />
        {input.trim() || attachmentPreview ? (
          <button
            className="send-message-btn"
            onClick={handleSend}
            disabled={uploading}
            aria-label="Send message"
          >
            {uploading ? (
              <span className="upload-spinner"></span>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            )}
          </button>
        ) : (
          <button
            className="rtc-mic-btn"
            onClick={startVoiceRecording}
            disabled={uploading}
            aria-label="Record voice message"
            type="button"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="9" y="2" width="6" height="12" rx="3"/>
              <path d="M19 10v2a7 7 0 01-14 0v-2"/>
              <line x1="12" y1="19" x2="12" y2="23"/>
              <line x1="8" y1="23" x2="16" y2="23"/>
            </svg>
          </button>
        )}
      </div>
      )}
    </div>
  );
};

export default RealtimeChatPanel;
